import { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

type Product = { id: number; name: string; modelNumber: string | null; category: string | null; warrantyYears: number; status: string; createdAt: string };

export default function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', modelNumber: '', category: '', warrantyYears: '2' });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});

  useEffect(() => {
    api.get<Product[]>('/admin/products').then(setProducts).finally(() => setLoading(false));
  }, []);

  async function syncBC() {
    setSyncing(true); setSyncMsg('');
    try {
      const r = await api.post<{ synced: number; inserted: number; updated: number; error: string | null }>('/admin/products/sync-bc', {});
      setSyncMsg(`✓ ${r.synced} synced (${r.inserted} new, ${r.updated} updated)`);
      api.get<Product[]>('/admin/products').then(setProducts);
    } catch (e: any) { setSyncMsg(`✗ ${e.message ?? 'Sync failed'}`); }
    finally { setSyncing(false); }
  }

  async function addProduct() {
    if (!form.name.trim()) return;
    const p = await api.post<Product>('/admin/products', {
      name: form.name.trim(),
      modelNumber: form.modelNumber.trim() || undefined,
      category: form.category.trim() || undefined,
      warrantyYears: Number(form.warrantyYears) || 2,
    });
    setProducts(prev => [p, ...prev]);
    setForm({ name: '', modelNumber: '', category: '', warrantyYears: '2' });
    setAdding(false);
  }

  async function saveEdit(id: number) {
    await api.patch(`/admin/products/${id}`, editForm);
    setProducts(p => p.map(x => x.id === id ? { ...x, ...editForm } : x));
    setEditId(null);
  }

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Products</h1>
        <div className="flex items-center gap-3 flex-wrap">
          {syncMsg && <span style={{ fontSize: 12, color: syncMsg.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{syncMsg}</span>}
          <button onClick={syncBC} disabled={syncing}
            style={{ fontSize: 12, padding: '6px 14px', border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#374151' }}>
            {syncing ? 'Syncing…' : '↻ Sync from BC'}
          </button>
          <button onClick={() => setAdding(a => !a)}
            style={{ fontSize: 12, padding: '6px 14px', borderRadius: 6, background: '#e00000', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            + Add product
          </button>
        </div>
      </div>

      {/* Add form */}
      {adding && (
        <div className="rounded-xl p-4 mb-4" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
          <p className="font-semibold text-sm mb-3" style={{ color: '#0b0d0e' }}>New product</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><label className="label">Name *</label><input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. MONTI Bristle Blaster" /></div>
            <div><label className="label">Model number</label><input className="input" value={form.modelNumber} onChange={e => setForm(f => ({ ...f, modelNumber: e.target.value }))} placeholder="e.g. MB-3000" /></div>
            <div><label className="label">Category</label><input className="input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Power Tools" /></div>
            <div><label className="label">Warranty years</label><input className="input" type="number" min="1" max="10" value={form.warrantyYears} onChange={e => setForm(f => ({ ...f, warrantyYears: e.target.value }))} /></div>
          </div>
          <div className="flex gap-3 mt-3">
            <button className="btn-primary text-sm" onClick={addProduct} disabled={!form.name.trim()}>Add product</button>
            <button onClick={() => setAdding(false)} style={{ fontSize: 14, color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="rounded-xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
        {loading ? <p style={{ padding: 24, color: '#8f9b9b', fontSize: 14 }}>Loading…</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 560 }}>
              <thead style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr style={{ textAlign: 'left' }}>
                  {['Product', 'Model', 'Category', 'Warranty', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8f9b9b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => editId === p.id ? (
                  <tr key={p.id} style={{ borderBottom: '1px solid #fafafa', background: '#fffbf5' }}>
                    <td style={{ padding: '10px 16px' }}><input className="input" style={{ fontSize: 13 }} value={editForm.name ?? p.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                    <td style={{ padding: '10px 16px' }}><input className="input" style={{ fontSize: 13 }} value={editForm.modelNumber ?? p.modelNumber ?? ''} onChange={e => setEditForm(f => ({ ...f, modelNumber: e.target.value }))} /></td>
                    <td style={{ padding: '10px 16px' }}><input className="input" style={{ fontSize: 13 }} value={editForm.category ?? p.category ?? ''} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} /></td>
                    <td style={{ padding: '10px 16px' }}><input className="input" style={{ fontSize: 13, width: 60 }} type="number" value={editForm.warrantyYears ?? p.warrantyYears} onChange={e => setEditForm(f => ({ ...f, warrantyYears: Number(e.target.value) }))} /></td>
                    <td style={{ padding: '10px 16px' }}>
                      <div className="flex gap-3">
                        <button onClick={() => saveEdit(p.id)} style={{ fontSize: 12, color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Save</button>
                        <button onClick={() => setEditId(null)} style={{ fontSize: 12, color: '#8f9b9b', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={p.id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 500, color: '#0b0d0e' }}>{p.name}</td>
                    <td style={{ padding: '12px 16px', color: '#8f9b9b', fontFamily: 'monospace', fontSize: 12 }}>{p.modelNumber ?? '—'}</td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{p.category ?? '—'}</td>
                    <td style={{ padding: '12px 16px', color: '#374151' }}>{p.warrantyYears}y</td>
                    <td style={{ padding: '12px 16px' }}>
                      <button onClick={() => { setEditId(p.id); setEditForm({}); }} style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Edit</button>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#8f9b9b' }}>No products yet. Add one above or sync from BC.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
